import Fuse, { FuseResult, IFuseOptions } from "fuse.js";
import { Props as ReactSelectProps } from "react-select";
import { isGroupedOption } from "common/vendor-wrappers/react-select/functions";
import {
  LabelledOption,
  LabelledOptionOrGroup,
} from "common/vendor-wrappers/react-select/types";
import { translateCategory } from "./translation";
import { Command } from "./types";

type SearchableCommandOption = LabelledOption<Command> & {
  translatedCategory?: string;
};

/**
 * Filters and sorts commands using fuzzy search based on a search term.
 * Handles both grouped and non-grouped react-select options.
 * Returns the original options structure if the search term is empty.
 * Returns a flat, sorted list (by relevance) of individual command options
 * if a search term is provided.
 *
 * @param options - An array of command options, possibly grouped.
 * @param searchTerm - The search term to filter by.
 * @returns A readonly array suitable for react-select props' options.
 */
export function searchCommands(
  options: ReadonlyArray<LabelledOptionOrGroup<Command>> | undefined,
  searchTerm: string,
): ReactSelectProps["options"] {
  if (!searchTerm) {
    return options || [];
  }

  if (!Array.isArray(options)) {
    return [];
  }

  const flatOptions: SearchableCommandOption[] = [];
  options.forEach((optionOrGroup) => {
    if (isGroupedOption(optionOrGroup)) {
      optionOrGroup.options.forEach((option: LabelledOption<Command>) => {
        if (option?.value) {
          flatOptions.push({
            ...option,
            translatedCategory: translateCategory(option.value.category),
          });
        }
      });
    } else if (optionOrGroup?.value) {
      flatOptions.push({
        ...optionOrGroup,
        translatedCategory: translateCategory(optionOrGroup.value.category),
      });
    }
  });

  const fuseOptions: IFuseOptions<SearchableCommandOption> = {
    keys: [
      { name: "label", weight: 1.0 },
      { name: "translatedCategory", weight: 0.5 },
    ],
    includeScore: true,
    threshold: 0.4, // threshold (0.0 = exact, 1.0 = match anything)
    ignoreLocation: true, // matching anywhere in the string
    shouldSort: true,
    useExtendedSearch: true,
  };

  const fuse = new Fuse(flatOptions, fuseOptions);
  const results: FuseResult<SearchableCommandOption>[] =
    fuse.search(searchTerm);

  return results.map((result) => result.item);
}
